import moment from 'moment';
import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import { getQueueTasks as _getQueueTasks } from '@/graphql/queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faSms,
  faEnvelope,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import ExpandButton from '@/components/ExpandButton';
import { getDashboardStats } from '@/apis/mockDbCrud';
import { dash } from '@/utils';
import useSubscription from '@/hooks/useSubscription';
import './Widgets.scss'

const client = generateClient();

const getQueueTasks = ({ filters, limit, sorting } = {}) => {
  return client.graphql({ query: _getQueueTasks, variables: { filters, limit, sorting }, sorting: { field: 'estimated_first_attempt_at', order: 'asc' } })
    .then(response => {
      return response.data.getQueueTasks?.queue_task
    });
};

/* ---------- helpers ---------- */

// Normalize text: lowercase + remove symbols/spaces
const normalize = (s = '') => s.toLowerCase().replace(/[^a-z0-9]/g, '');

// Map aliases to fixed groups: call | sms | email
function mapToGroup(typeLabel = '') {
  const k = normalize(typeLabel);
  if (['call', 'phone', 'phonecall', 'zoom', 'googlemeet', 'meet'].includes(k)) return 'call';
  if (['sms', 'text'].includes(k)) return 'sms';
  if (['email', 'mail'].includes(k)) return 'email';
  return null; // unknown
}

const ICON_BY_GROUP = {
  call: faPhone,
  sms: faSms,
  email: faEnvelope,
};

const DISPLAY_BY_GROUP = {
  call: 'Call',
  sms: 'Text',
  email: 'Email',
};

// Fallback cycle if no type in data
const TYPE_CYCLE = ['Call', 'Text', 'Email'];

// function formatDateTime(date, time) {
//   try {
//     const dt = new Date(`${date ?? ''} ${time ?? ''}`.trim());
//     if (isNaN(dt.getTime())) return 'Invalid Date';
//     return dt.toLocaleString('en-US', {
//       month: 'short',
//       day: 'numeric',
//       hour: 'numeric',
//       minute: '2-digit',
//       hour12: true,
//     });
//   } catch {
//     return 'Invalid Date';
//   }
// }

function formatDateTime(date, time) {
  try {
    const dt = new Date(`${date ?? ''} ${time ?? ''}`.trim());
    if (isNaN(dt.getTime())) return 'Invalid Date';

    return dt.toLocaleString('en-US', {
      weekday: 'long',     
      year: 'numeric',     
      month: 'long',       
      day: 'numeric',      
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return 'Invalid Date';
  }
}

/* ---------- component ---------- */

const AppointmentsCard = () => {
  const isConstructed = useRef(false);
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  if (!isConstructed.current) {
    isConstructed.current = true;
    getQueueTasks({ filters: { 'status': { ne: 'COMPLETED' } }, limit: 100, sorting: { field: 'estimated_first_attempt_at', order: 'asc' } })
      .then(res => {
        setRows(res);
      });
  }

  const filters_ref = useRef({ sorting: { field: 'estimated_first_attempt_at', order: 'asc' } });
  useSubscription({ filters_ref, rankings_key: 'queue_task', setRows });

  const handleExpand = () => {
    navigate('/TopOutreachMethodsExpended');
  };

  return (
    <div
      className="bg-white px-4 py-3 rounded-3 shadow-sm d-flex flex-column dashboard-card"
      style={{  border: '1px solid #e0e0e0' }}
    >
         {/* Header */}
            <div
                   className="d-flex justify-content-between align-items-center"
                   style={{ marginBottom: '0.25rem' }}
                 >
                   <h6
                     className="fw-semibold fs-5 mb-0"
                     style={{
                       color: '#2E2868',
                       display: 'flex',
                     position: 'relative',
                     position: 'relative',
                       top: '-5.5px'
                       
                     }}
                   >
                     Agent Action Queue
                   </h6>
           
                   <div style={{
                     display: 'flex',
                     position: 'relative'
                   }}>
                     <div style={{
                       position: 'relative',
                      top: '-5px'
                     }}>
                       <ExpandButton onClick={handleExpand} />
                     </div>
                   </div>
           
                 </div>
     
           {/* Divider */}
           <div
             style={{
               height: '2px',
               backgroundColor: '#f3f4f6',
               marginTop: '0.1rem',
               marginBottom: '0.25rem',
               width: 'calc(100% + 3rem)',
               marginLeft: '-1.5rem',
             }}
           />
      {/* Appointments List (scrollable) */}
      <div
        className="d-flex flex-column gap-2 flex-grow-1"
        style={{ maxHeight: 320, overflowY: 'auto' }}
      >
        {rows.length === 0 && (
          <div className="text-muted text-center">No appointments available.</div>
        )}

        {rows.map((appt, idx) => {
          // Raw type from data or fallback cycle
          const raw = appt?.type || appt?.channel || TYPE_CYCLE[idx % 3];

          // Map to unified group (call/sms/email) + icon/label
          const group = mapToGroup(raw);
          const icon = group ? ICON_BY_GROUP[group] : faCircleQuestion;
          const label = group ? DISPLAY_BY_GROUP[group] : raw;

          return (
           <div
              key={appt.id ?? idx}
              className="border rounded px-3 py-2"
            >
              <div className="row align-items-center">

                {/* Channel */}
                <div className="col text-muted small">
                  {appt.channel}
                </div>

                {/* Subject */}
                <div className="col fw-semibold text-truncate">
                  <Link className="text-decoration-none text-muted small" to={`/app/pipeline/subjects/${appt.subject_id}?tab=contacts`}>{appt.full_name_first}</Link>
                </div>

                {/* Date */}
                <div className="col text-muted small">
                  {appt.estimated_first_attempt_at ? moment(appt.estimated_first_attempt_at).format('M/D/YY h:mm:ss A') : dash}
                </div>

                {/* Status */}
                <div className="col-auto ms-auto d-flex justify-content-end align-items-center gap-2" style={{width: '120px'}}>
                  <span className="text-dark">{appt.status}</span>
                </div>

              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppointmentsCard;
